import {
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { Tag } from '../Tag/Tag';
import styles from './InputField.module.css';

/**
 * Figma: `Input field` (825:19065) — a labelled single-line text field, 28px
 * tall, with an optional leading icon, a hint line under it, and a tag-entry
 * mode. 14 variants across four variant properties and six toggles; the set
 * carries no annotations.
 *
 * Only `Type` becomes an enum prop. The rest map to real state:
 *
 * - `Size=sm` is the only option, so there is nothing to choose. Omitted rather
 *   than shipped as a one-member union.
 * - `State=Placeholder | Filled` is whether the field has a value.
 * - `State=Focused` is focus.
 * - `State=Disabled` is the `disabled` prop.
 * - `Destructive=True` is `errorMessage` being present — the red ring, the alert
 *   glyph and the red message are one condition, not three.
 *
 * Figma fixes the frame at 320 (`Type=Default`) and 400 (`Type=Tags`). Here it
 * fills its container, which is what a form control has to do to sit in a real
 * layout — the same deviation `Select` makes.
 */
export type InputFieldType = 'default' | 'tags';

/** Force a state for documentation and visual regression only. */
export type InputFieldState = 'default' | 'focused';

export type InputFieldProps = {
  /** Figma `Type`. `tags` renders `value` as removable `Tag`s before the caret. */
  type?: InputFieldType;
  /** Figma's `Label` toggle. Omit for a bare field. */
  label?: string;
  /** Figma's `↳ Required *` toggle — the asterisk after the label. */
  required?: boolean;
  /** Figma's `Hint text` toggle — the line under the field. */
  hint?: string;
  /**
   * Figma `Destructive=True`. The message replaces `hint`, the ring goes
   * `component/input/border-error` and the `crisis alerts` glyph appears.
   */
  errorMessage?: string;
  /**
   * Figma `⬅️ Icon leading` + its `Instance` instance-swap slot. Omit to render
   * without one. `Type=Tags` has no leading icon in any variant.
   */
  iconLeading?: ReactNode;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Figma `State=Disabled`. */
  disabled?: boolean;
  /**
   * Masks the value — a native `type="password"`.
   *
   * **Not in Figma**, which has no password variant: the Settings frames
   * (825:22105) draw the two password fields as an ordinary `Type=Default`
   * field whose *text layer* happens to read `*************`. Mirroring that
   * literally would give a field that types a password in the clear, which is a
   * real failure rather than a fidelity one, so the masking is the native
   * behaviour and the asterisks are the browser's.
   *
   * It changes no geometry — a password input is laid out exactly like a text
   * one — so the parity fixtures are untouched.
   *
   * Ignored by `type="tags"`, which has nothing to mask.
   */
  secure?: boolean;
  /**
   * `Type="tags"` only — the tags drawn before the caret. Controlled alongside
   * `onTagsChange`; omit both to let the field own them.
   */
  tags?: string[];
  defaultTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  name?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'url' | 'search' | 'numeric';
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * Accessible name for a field with no visible `label`. Without it the input is
   * announced unnamed. Ignored when `label` is given. Not in Figma.
   */
  'aria-label'?: string;
  state?: InputFieldState;
  className?: string;
};

export function InputField({
  type = 'default',
  label,
  required = false,
  hint,
  errorMessage,
  iconLeading,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  disabled = false,
  secure = false,
  tags,
  defaultTags,
  onTagsChange,
  name,
  inputMode,
  onChange,
  onFocus,
  onBlur,
  'aria-label': ariaLabel,
  state,
  className,
}: InputFieldProps) {
  const inputId = useId();
  const messageId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [ownValue, setOwnValue] = useState(defaultValue ?? '');
  const currentValue = value ?? ownValue;

  const [ownTags, setOwnTags] = useState<string[]>(defaultTags ?? []);
  const currentTags = tags ?? ownTags;

  const destructive = errorMessage !== undefined;
  const showTags = type === 'tags' && currentTags.length > 0;

  const commitValue = (next: string) => {
    if (value === undefined) setOwnValue(next);
    onValueChange?.(next);
  };

  const commitTags = (next: string[]) => {
    if (tags === undefined) setOwnTags(next);
    onTagsChange?.(next);
  };

  /*
   * Not in Figma — the design draws a filled tag field but says nothing about
   * how a tag gets added or removed. Enter commits the typed text, and Backspace
   * on an empty field removes the last tag, which is the convention every
   * tag input follows.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (type !== 'tags' || disabled) return;

    if (event.key === 'Enter' && currentValue.trim() !== '') {
      event.preventDefault();
      commitTags([...currentTags, currentValue.trim()]);
      commitValue('');
      return;
    }

    if (event.key === 'Backspace' && currentValue === '' && currentTags.length > 0) {
      commitTags(currentTags.slice(0, -1));
    }
  };

  const removeTag = (index: number) => {
    commitTags(currentTags.filter((_, i) => i !== index));
    inputRef.current?.focus();
  };

  const inputClasses = [
    styles.input,
    disabled ? styles.disabled : null,
    destructive ? styles.destructive : null,
    showTags ? styles.hasTags : null,
    /*
     * `:focus-within` is live only when no state is forced, so focus reacts in
     * the app and stays inert in a states story. A forced focus on a disabled
     * field is ignored — Figma has no such variant.
     */
    !disabled && state === 'focused' ? styles.focused : null,
    !disabled && state === undefined ? styles.interactive : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={styles.inputWithLabel}>
        {label !== undefined && (
          <div className={styles.labelWrapper}>
            <label className={styles.label} htmlFor={inputId}>
              {label}
            </label>
            {/*
             * Decorative: `required` on the input is what assistive tech reads,
             * so the asterisk repeating it would be noise.
             */}
            {required && (
              <span className={styles.asterisk} aria-hidden="true">
                *
              </span>
            )}
          </div>
        )}

        {/*
         * Not in Figma — clicking the field's padding, or the gap between tags,
         * puts the caret in the input. Without this only the 12px-tall input
         * itself is a hit target inside a 28px box.
         */}
        <div
          className={inputClasses}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) inputRef.current?.focus();
          }}
        >
          <div className={styles.content}>
            {iconLeading !== undefined && <span className={styles.iconSlot}>{iconLeading}</span>}

            {showTags && (
              <span className={styles.tags}>
                {currentTags.map((tag, index) => (
                  <Tag
                    key={tag}
                    onRemove={disabled ? undefined : () => removeTag(index)}
                    removeLabel={`Remove ${tag}`}
                  >
                    {tag}
                  </Tag>
                ))}
              </span>
            )}

            <input
              ref={inputRef}
              id={inputId}
              className={styles.control}
              /* `tags` has nothing to mask — see `secure`. */
              type={secure && type !== 'tags' ? 'password' : 'text'}
              name={name}
              inputMode={inputMode}
              placeholder={placeholder}
              value={currentValue}
              disabled={disabled}
              /*
               * Not the DOM `required` at `type="tags"` — the field's value is
               * the tag list and this element is only the caret, so the
               * constraint could never be met and would block `submit` on the
               * whole form. `aria-required` keeps what the asterisk promises.
               * Same defect and same fix as `TextareaField`, which documents it
               * at length; found there by measurement on 2026-09-21.
               */
              required={type === 'tags' ? undefined : required}
              aria-required={type === 'tags' && required ? true : undefined}
              aria-label={label === undefined ? ariaLabel : undefined}
              aria-invalid={destructive || undefined}
              aria-describedby={hint !== undefined || destructive ? messageId : undefined}
              onChange={(event) => {
                commitValue(event.target.value);
                onChange?.(event);
              }}
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          </div>

          {/*
           * Figma `crisis alerts`, 16x16 in `icon/error`. Decorative — the
           * message below says the same thing in words, and `aria-invalid`
           * already marks the field.
           */}
          {destructive && (
            <span className={styles.errorIcon}>
              <Icon name="crisisAlerts" />
            </span>
          )}
        </div>
      </div>

      {/*
       * Figma draws `Hint text` and the error message as two layers on separate
       * variants, never both — `Destructive=True` swaps one for the other.
       */}
      {destructive ? (
        <p className={styles.errorMessage} id={messageId}>
          {errorMessage}
        </p>
      ) : (
        hint !== undefined && (
          <p className={styles.hint} id={messageId}>
            {hint}
          </p>
        )
      )}
    </div>
  );
}
